import { BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { QIcon_pause_single, QIcon_play_single } from '@repo/icons/index'
import { useContext, useEffect, useState } from 'react'
import { useConversationSetting } from '../../../../modules/apis/student/conversation'
import { useIsOkToUse } from '../../../../modules/hooks/useAppVersionStatus'
import { n_ai_tutor, n_common } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import WaveForm from '../../../common/audio/WaveForm'
import { SoundContext, SoundProvider } from '../../../common/audio/play/Audio'
import { HorizontalStack } from '../Common'

const ExpressionCard = ({
  expression,
  subtitleLang,
  setProgress,
  paused,
  setPaused,
  active,
  restarted,
  setRestarted,
}) => {
  const setting = useConversationSetting()
  const sound = useContext(SoundContext)

  const isOkToUse = useIsOkToUse()

  useEffect(() => {
    sound.speed = setting.data?.speed || 1
  }, [setting])

  useEffect(() => {
    function handleMessage(event) {
      let data
      try {
        data = JSON.parse(event.data)
      } catch (e) {
        console.error('Invalid message format:', e)
        return
      }

      if (data.type === 'audioEnded') {
        console.log('오디오 재생 끝!')
        handleAudioEnded()
      }
    }

    window.addEventListener('message', handleMessage)
    document.addEventListener('message', handleMessage) // iOS 대응

    // 클린업
    return () => {
      window.removeEventListener('message', handleMessage)
      document.removeEventListener('message', handleMessage)
    }
  }, [])

  function handleAudioEnded() {
    setProgress(1)
    setRestarted(false)
    setPaused(true) // ← 추가!
  }

  // let isMulticampus = customerType?.isMulticampus

  useEffect(() => {
    if (!active) return
    if (!expression.audio) return

    let intv
    if (!isOkToUse) {
      intv = setInterval(() => {
        setProgress(sound.progress)

        if (sound.progress >= 1) {
          clearInterval(intv)
        }
      }, 100)

      // 이거로 이제..
      sound.ctx
        .refresh()
        .then(() => sound.play([expression.audio]))
        .then(() => {
          clearInterval(intv)
          setProgress(1)
        })
    } else {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'playAudio', url: expression.audio }))
    }

    return () => {
      // setProgress(0)
      // sound.stop()
      if (!isOkToUse) {
        clearInterval(intv)

        sound.stop()
      } else {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'stopAudio' }))
      }
    }
  }, [expression.audio, active])

  useEffect(() => {
    if (!active) return
    if (!restarted) return
    if (!expression.audio) return

    let intv

    if (!isOkToUse) {
      intv = setInterval(() => {
        setProgress(sound.progress)
        if (sound.progress >= 1) {
          clearInterval(intv)
        }
      }, 100)
      sound.ctx
        .refresh()
        .then(() => sound.play([expression.audio]))
        .then(() => {
          clearInterval(intv)
          setProgress(1)
          setRestarted(false)
        })
    } else {
      //only legit app
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'playAudio', url: expression.audio }))
    }

    return () => {
      // setProgress(0)

      if (!isOkToUse) {
        // setRestarted(false)
        clearInterval(intv)
        sound.stop()
      } else {
        // setRestarted(false)
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'stopAudio' }))
      }
    }
  }, [restarted, active])

  useEffect(() => {
    if (!active) return

    if (!isOkToUse) {
      // 멀캠이면 일단 동일.
      if (paused) {
        if (!sound.pausedAt) sound.pause()
      } else {
        if (sound.pausedAt) sound.resume()
      }
    } else {
      if (paused) {
        //누르면 바로 반응을 해야 할 것 같은데 흠.
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'pauseAudio' }))
      } else {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'resumeAudio' }))
      }
    }
  }, [paused, active])

  return (
    <div className=" w-full h-full bg-white rounded-[16px] p-[20px] flex flex-col gap-[16px] items-center justify-center border-[1.5px] border-gray-200">
      <div className="flex flex-col gap-[16px] items-center justify-start self-stretch flex-1 pt-[20px]">
        <div className="flex flex-col items-center justify-start self-stretch shrink-0">
          <div className="bg-gray-100 rounded-[100px] p-4 flex flex-col items-center justify-center shrink-0 w-[60px] h-[60px] relative">
            <WaveForm sound={sound} count={4} />
          </div>
        </div>
        <div className="flex flex-col gap-[12px] items-center justify-center self-stretch shrink-0">
          <div className="text-gray-500 text-center Body1-16Md self-stretch">{expression.type}</div>
          <div className="text-gray-700 text-center Title1-20Bd !text-[22px] self-stretch">{expression.content}</div>
        </div>
      </div>
      <div className="bg-gray-300 p-[16px] flex items-center justify-center self-stretch shrink-0 rounded-lg Body2-16Md">
        <div className="text-black text-left Body2-14Md flex-1">{expression.script[subtitleLang]}</div>
      </div>
    </div>
  )
}

const ExpressionActivity = ({ expressions, nextActivity, subtitleLang, isActive }) => {
  const { t } = useTranslation()
  const [expression, setExpression] = useState(0)
  const [progress, setProgress] = useState(0)

  // 처음에 paused는 false. 즉, 일단 재생 허용 상태
  const [paused, setPaused] = useState(false) //처음에 paused
  const [restarted, setRestarted] = useState(false)

  const next = () => {
    if (expression < expressions.length - 1) {
      setPaused(false)
      setExpression(expression + 1)
    } else {
      setPaused(true)
      nextActivity()
    }
  }

  const prev = () => {
    if (expression > 0) {
      setPaused(false)
      setExpression(expression - 1)
    }
  }

  const restart = () => {
    setPaused(false)
    setRestarted(true)
    setProgress(0)
  }

  return (
    <div className="pt-[32px] pb-[34px] px-[20px] flex flex-col gap-[12px] items-start justify-start self-stretch flex-1 bg-gray-100 h-full">
      <div className="flex flex-col gap-[20px] items-center justify-start self-stretch flex-1">
        <div className="flex flex-col gap-[8px] items-start justify-start self-stretch flex-1">
          <div className="flex-1 grid grid-cols-1 min-h-[160px] w-full">
            <HorizontalStack
              menu={expressions.map((e, i) => {
                const Comp = () => (
                  <SoundProvider key={`expression-card-${i}`}>
                    <ExpressionCard
                      expression={e}
                      subtitleLang={subtitleLang}
                      setProgress={setProgress}
                      paused={paused}
                      setPaused={setPaused}
                      active={i === expression && isActive}
                      restarted={restarted}
                      setRestarted={setRestarted}
                    />
                  </SoundProvider>
                )
                return Comp
              })}
              current={expression}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-[72px] items-center justify-center self-stretch shrink-0 relative">
        <div className="flex flex-row gap-[12px] items-start justify-start shrink-0 relative">
          <div className="bg-white rounded-[100px] flex flex-row gap-[4px] items-center justify-center shrink-0 w-[32px] h-[32px] relative">
            <div className="shrink-0 w-[16px] h-[16px] relative">
              {progress < 1 ? (
                paused ? (
                  <QIcon_play_single onClick={() => setPaused(false)} width={16} height={16} color={COLORS.BLACK} />
                ) : (
                  <QIcon_pause_single onClick={() => setPaused(true)} width={16} height={16} color={COLORS.BLACK} />
                )
              ) : (
                <QIcon_play_single onClick={restart} width={16} height={16} color={COLORS.BLACK} />
              )}
            </div>
          </div>
          <div className="bg-white rounded-[100px] p-[12px] flex flex-row gap-[4px] items-center justify-center shrink-0 relative">
            {expressions.map((e, i) =>
              expression === i ? (
                <div className="bg-gray-500 rounded-[100px] shrink-0 w-[60px] h-[6px] relative overflow-hidden" key={i}>
                  <div
                    className="bg-black rounded-[100px] w-[100%] h-[100%] absolute"
                    style={{ width: `${progress * 100}%` }}
                  ></div>
                </div>
              ) : (
                <div className="bg-gray-500 rounded-[100px] shrink-0 w-[6px] h-[6px] relative" key={i}></div>
              )
            )}
          </div>
        </div>
      </div>
      <div className="w-full sticky bottom-[20px] pt-[20px] left-[0px] flex flex-row gap-[4px] mb-[12px]">
        {expression > 0 && (
          <div className="min-w-[90px] w-[33.2%]">
            <DSButton onClick={prev} type={BUTTON_TYPE.OUTLINE}>
              <DSButton.Label>{t(n_common.btn_back)}</DSButton.Label>
            </DSButton>
          </div>
        )}
        <DSButton className="w-full" onClick={next}>
          <DSButton.Label>{t(n_ai_tutor.study_activity.continue_btn)}</DSButton.Label>
        </DSButton>
      </div>
    </div>
  )
}

export default ExpressionActivity
